/**
 * Shared presentation helpers for time-phased planning surfaces.
 */
import type { PlacementStatus } from '../../../lib/timePhasedPlanning.types';

export const PLACEMENT_STATUS_LABELS: Record<PlacementStatus, string> = {
  scheduled: 'Scheduled',
  slipped: 'Slipped',
  unscheduled: 'Unscheduled',
  blocked: 'Blocked',
  supply_exhausted: 'Supply exhausted',
  below_line: 'Below the line',
};

export const PLACEMENT_STATUS_CLASS: Record<PlacementStatus, string> = {
  scheduled: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  slipped: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  unscheduled: 'bg-gray-50 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400',
  blocked: 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400',
  supply_exhausted: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400',
  below_line: 'bg-gray-100 text-gray-500 dark:bg-gray-800/40 dark:text-gray-400',
};

export function utilizationHeatClass(pct: number): string {
  if (pct >= 100) return 'bg-red-500';
  if (pct >= 85) return 'bg-amber-400';
  if (pct >= 60) return 'bg-yellow-300';
  return 'bg-emerald-300';
}

export function parseSupplyCsv(text: string): {
  resourceType: string;
  bucketStart: string;
  supplyHours: number;
  resourceName?: string;
  bucketEnd?: string;
}[] {
  const lines = text.trim().split(/\r?\n/).filter(Boolean);
  if (lines.length < 2) return [];
  const header = lines[0].toLowerCase();
  const hasHeader = header.includes('resource_type');
  const dataLines = hasHeader ? lines.slice(1) : lines;
  return dataLines
    .map((line) => {
      const parts = line.split(',').map((p) => p.trim().replace(/^"|"$/g, ''));
      if (parts.length < 3) return null;
      if (parts.length >= 5) {
        const [resourceType, resourceName, bucketStart, bucketEnd, supplyHours] = parts;
        const hours = Number(supplyHours);
        if (!resourceType || !bucketStart || Number.isNaN(hours)) return null;
        return {
          resourceType,
          bucketStart,
          bucketEnd: bucketEnd || undefined,
          supplyHours: hours,
          resourceName: resourceName || undefined,
        };
      }
      const [resourceType, bucketStart, supplyHours] = parts;
      const hours = Number(supplyHours);
      if (!resourceType || !bucketStart || Number.isNaN(hours)) return null;
      return { resourceType, bucketStart, supplyHours: hours };
    })
    .filter((r): r is NonNullable<typeof r> => r != null);
}

export function downloadTextFile(filename: string, content: string, mime = 'text/csv'): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
